import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Home, Bug, Send, AlertCircle, Upload, X, FileIcon } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";

const ReportBug = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [formData, setFormData] = useState({
    name: user ? `${user.firstName} ${user.lastName}` : "",
    email: user?.email || "",
    message: "",
    priority: "medium",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.message.trim()) {
      toast({
        title: "Error",
        description: "Please describe the issue you're experiencing.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Create a scope to add attachments
      await Sentry.withScope(async (scope) => {
        // Add user information
        scope.setUser({
          email: formData.email,
          name: formData.name,
        });

        // Add tags and context
        scope.setTags({
          type: "bug_report",
          priority: formData.priority,
        });

        scope.setContext("feedback", {
          message: formData.message,
          contact_email: formData.email,
          name: formData.name,
          priority: formData.priority,
          attachments_count: attachments.length,
        });

        // Convert and add attachments to the scope
        for (const file of attachments) {
          try {
            // Read file as ArrayBuffer
            const arrayBuffer = await file.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);

            // Add attachment to scope
            scope.addAttachment({
              filename: file.name,
              data: uint8Array,
              contentType: file.type,
            });
          } catch (error) {
            console.error(`Error processing attachment ${file.name}:`, error);
          }
        }

        // Capture the event with attachments
        const eventId = Sentry.captureMessage("User Bug Report", {
          level: formData.priority === "high" ? "error" : "warning",
        });

        // Also send feedback (this won't have attachments but links to the event)
        if (eventId) {
          try {
            Sentry.captureFeedback({
              name: formData.name,
              email: formData.email,
              message: formData.message,
              associatedEventId: eventId,
            });
          } catch (error) {
            console.error("Error capturing feedback:", error);
          }
        }
      });

      toast({
        title: "Bug report submitted",
        description: "Thank you for your feedback. We'll look into this issue.",
      });

      // Reset form
      setFormData({
        ...formData,
        message: "",
        priority: "medium",
      });
      setAttachments([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error submitting bug report:", error);
      toast({
        title: "Submission failed",
        description: "There was an error submitting your bug report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const maxSize = 10 * 1024 * 1024; // 10MB
    const maxFiles = 5;

    // Filter out files that are too large
    const validFiles = files.filter((file) => {
      if (file.size > maxSize) {
        toast({
          title: "File too large",
          description: `${file.name} is too large. Maximum size is 10MB.`,
          variant: "destructive",
        });
        return false;
      }
      return true;
    });

    // Check total number of files
    if (attachments.length + validFiles.length > maxFiles) {
      toast({
        title: "Too many files",
        description: `You can only attach up to ${maxFiles} files.`,
        variant: "destructive",
      });
      return;
    }

    setAttachments([...attachments, ...validFiles]);
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const getFileIcon = (file: File) => {
    const type = file.type;
    
    // Check if file will render in Sentry UI
    const renderableTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/avif',
      'video/webm', 'video/mp4',
      'text/plain', 'text/css', 'text/csv', 'text/html', 'text/javascript',
      'application/json'
    ];
    
    const isRenderable = renderableTypes.includes(type);
    
    return {
      icon: FileIcon,
      className: isRenderable ? "text-green-600" : "text-muted-foreground",
      tooltip: isRenderable ? "Will render in Sentry UI" : "Will be available for download"
    };
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav
        className="flex items-center text-sm text-muted-foreground mb-2"
        aria-label="Breadcrumb"
      >
        <Link
          to="/"
          className="hover:underline text-primary font-medium flex items-center"
        >
          <Home className="h-4 w-4 mr-1" />
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground font-semibold">Report a Bug</span>
      </nav>

      {/* Page Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="bg-orange-100 p-3 rounded-lg">
            <Bug className="h-6 w-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Report a Bug</h1>
            <p className="text-muted-foreground">
              Help us improve by reporting issues you encounter
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* Bug Report Form */}
        <Card className="md:col-span-2">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Send className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>Describe the Issue</CardTitle>
                <CardDescription>
                  Please provide as much detail as possible about the bug you encountered
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Your Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Your Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    placeholder="john@example.com"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) =>
                    setFormData({ ...formData, priority: value })
                  }
                >
                  <SelectTrigger id="priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low - Minor issue</SelectItem>
                    <SelectItem value="medium">Medium - Affects usability</SelectItem>
                    <SelectItem value="high">High - Critical issue</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Issue Description</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  placeholder="Please describe what happened, what you expected to happen, and any steps to reproduce the issue..."
                  rows={8}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Include details like what page you were on, what you clicked, and any error messages you saw.
                </p>
              </div>

              {/* File Attachments */}
              <div className="space-y-2">
                <Label>Attachments (Optional)</Label>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={attachments.length >= 5}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Add Files
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      className="hidden"
                      onChange={handleFileSelect}
                      accept="image/jpeg,image/png,image/gif,image/webp,image/avif,video/webm,video/mp4,text/plain,text/css,text/csv,text/html,text/javascript,application/json,.json,.txt,.html,.css,.csv,.js"
                    />
                    <span className="text-xs text-muted-foreground">
                      Max 5 files, 10MB each
                    </span>
                  </div>

                  {/* Attachment List */}
                  {attachments.length > 0 && (
                    <div className="space-y-2">
                      {attachments.map((file, index) => {
                        const fileIcon = getFileIcon(file);
                        const Icon = fileIcon.icon;
                        return (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 rounded-xl border bg-card shadow-sm hover:shadow-md transition-all duration-200"
                            title={fileIcon.tooltip}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <Icon className={`h-4 w-4 flex-shrink-0 ${fileIcon.className}`} />
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {file.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {formatFileSize(file.size)}
                                </p>
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeAttachment(index)}
                              className="flex-shrink-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground">
                    Attach screenshots (PNG, JPEG, GIF, WebP), videos (WebM, MP4), or text files (TXT, JSON, HTML, CSS, CSV, JS) that help explain the issue.
                  </p>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full md:w-auto btn-gradient shadow-sophisticated"
              >
                {isSubmitting ? (
                  <>Submitting...</>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Bug Report
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Tips Card */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-primary" />
                </div>
                <CardTitle className="text-lg">Reporting Tips</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Be Specific</p>
                  <p className="text-muted-foreground text-xs">
                    Describe exactly what you were doing when the bug occurred
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Steps to Reproduce</p>
                  <p className="text-muted-foreground text-xs">
                    List the steps needed to recreate the issue
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Expected vs Actual</p>
                  <p className="text-muted-foreground text-xs">
                    Explain what you expected to happen vs what actually happened
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Error Messages</p>
                  <p className="text-muted-foreground text-xs">
                    Include any error messages or unusual behavior you noticed
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Attach Files</p>
                  <p className="text-muted-foreground text-xs">
                    Screenshots, videos, and text files render directly in Sentry
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              <strong>Supported files:</strong> Images (PNG, JPEG, GIF, WebP, AVIF), Videos (WebM, MP4), Text files (TXT, JSON, HTML, CSS, CSV, JS) will display directly in Sentry.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    </div>
  );
};

export default ReportBug;
